import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { setTokens } from "@/lib/rehearsalRoomAuth";

const BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Master v5 Prompt 30 — public page a client lands on from their
 * "Invite new client" email. Sets a password against the token from
 * the link; on success the backend has already created their Workspace
 * (if the invite carried one), so `data.redirect_to` (default
 * `/rehearsal-room`) sends them straight into it via the existing
 * single-membership home-resolution logic.
 */
export default function AcceptInvitePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [terminal, setTerminal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BASE}/api/portal/invites/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, name, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || "This invite link is invalid or has expired.");
        setTerminal(true);
        return;
      }
      setTokens(data.access_token, data.refresh_token);
      navigate(data.redirect_to || "/rehearsal-room", { replace: true });
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-paper px-6" data-testid="accept-invite-missing-token">
        <div className="text-center">
          <p className="text-sm text-ink-soft mb-4">This invite link is missing its token. Please use the link from your invite email.</p>
          <Button onClick={() => navigate("/rehearsal-room/login")} data-testid="accept-invite-missing-token-login">Go to login</Button>
        </div>
      </section>
    );
  }

  if (terminal) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-paper px-6" data-testid="accept-invite-terminal">
        <div className="w-full max-w-sm text-center border border-ink/15 bg-paper px-8 py-10 shadow-[4px_8px_28px_rgba(27,27,24,0.08)]">
          <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-3">InterAct Globally</p>
          <p className="text-sm text-red-700 mb-6" data-testid="accept-invite-error">{error}</p>
          <p className="text-sm text-ink-soft mb-4">If you already have an account, sign in below. Otherwise, ask your coach to send a fresh invite.</p>
          <Button onClick={() => navigate("/rehearsal-room/login")} className="w-full" data-testid="accept-invite-terminal-login">Go to login</Button>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen flex items-center justify-center bg-paper px-6" data-testid="accept-invite-page">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm border border-ink/15 bg-paper px-8 py-10 shadow-[4px_8px_28px_rgba(27,27,24,0.08)]"
      >
        <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-3">InterAct Globally</p>
        <h1 className="font-serif text-2xl text-ink mb-2">Welcome to the Rehearsal Room</h1>
        <p className="text-sm text-ink-soft mb-6">Set your name and password to get started.</p>

        <div className="space-y-4">
          <div>
            <Label htmlFor="accept-invite-name">Your name</Label>
            <Input id="accept-invite-name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="accept-invite-name" />
          </div>
          <div>
            <Label htmlFor="accept-invite-password">Password</Label>
            <Input id="accept-invite-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="accept-invite-password" />
          </div>
          <div>
            <Label htmlFor="accept-invite-confirm-password">Confirm password</Label>
            <Input id="accept-invite-confirm-password" type="password" required minLength={8} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="accept-invite-confirm-password" />
          </div>
        </div>

        {error && <p className="mt-4 text-sm text-red-700" data-testid="accept-invite-error">{error}</p>}

        <Button type="submit" disabled={submitting} className="w-full mt-6" data-testid="accept-invite-submit">
          {submitting ? "Setting up your account…" : "Set password & get started"}
        </Button>
      </form>
    </section>
  );
}
